import { unpackToDateTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import {
  FormFieldConfig,
  FormFieldValue,
  FormSubmissionFieldValue,
  ExpandedFormFieldConfig,
  FormOutlineNode,
  FormOutlineSection,
} from './form-responses-types';

export const parseUserAgent = (ua: string | null | undefined): { browser: string; os: string; device: string } => {
  if (!ua) return { browser: '-', os: '-', device: '-' };

  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR/')) browser = 'Opera';

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  if (ua.includes('Mobile') || ua.includes('Android')) device = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad')) device = 'Tablet';

  return { browser, os, device };
};

export const formatSubmissionDate = (dateStr: string): string => {
  const dt = unpackToDateTime(dateStr);
  const formattedDate = dt.toLocaleString(DateTime.DATE_MED);
  const formattedTime = dt.toFormat('h:mm a');
  return `${formattedDate}, ${formattedTime}`;
};

export const getFieldValue = (
  submission: { formFieldValues?: FormFieldValue[] },
  taskFieldId: string
): string => {
  const fieldValue = submission.formFieldValues?.find((fv) => fv.taskFieldId === taskFieldId);
  return fieldValue?.value || '-';
};

export const sortFieldValuesByFormOrder = (
  fieldValues: FormSubmissionFieldValue[],
  formFields: FormFieldConfig[]
): FormSubmissionFieldValue[] => {
  if (!fieldValues || !formFields.length) return fieldValues || [];
  const orderMap = new Map(formFields.map((f, idx) => [f.taskFieldId, idx]));
  return [...fieldValues].sort((a, b) => {
    const orderA = orderMap.get(a.taskFieldId) ?? 999;
    const orderB = orderMap.get(b.taskFieldId) ?? 999;
    return orderA - orderB;
  });
};

export const escapeCSV = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const getGroupInstanceIds = (
  submission: { formFieldValues?: FormFieldValue[] },
  groupFieldIds: Set<string>
): string[] => {
  if (!submission.formFieldValues) return [];
  const instanceIds = new Set<string>();
  submission.formFieldValues.forEach((fv) => {
    if (fv.groupInstanceId && groupFieldIds.has(fv.taskFieldId)) {
      instanceIds.add(fv.groupInstanceId);
    }
  });
  return Array.from(instanceIds);
};

export const getGroupFieldValue = (
  submission: { formFieldValues?: FormFieldValue[] },
  taskFieldId: string,
  groupInstanceId: string
): string => {
  const fieldValue = submission.formFieldValues?.find(
    (fv) => fv.taskFieldId === taskFieldId && fv.groupInstanceId === groupInstanceId
  );
  return fieldValue?.value || '-';
};

export const buildExpandedColumns = (
  formFields: FormFieldConfig[],
  submissions: Array<{ formFieldValues?: FormFieldValue[] }>
): ExpandedFormFieldConfig[] => {
  const groups = new Map<string, FormFieldConfig[]>();
  const nonGroupFields: FormFieldConfig[] = [];

  formFields.forEach((field) => {
    if (field.groupFormItemId) {
      const existing = groups.get(field.groupFormItemId) || [];
      existing.push(field);
      groups.set(field.groupFormItemId, existing);
    } else {
      nonGroupFields.push(field);
    }
  });

  if (groups.size === 0) {
    return formFields.map((f) => ({ ...f, instanceIndex: 0, expandedName: f.name }));
  }

  const maxInstancesPerGroup = new Map<string, number>();
  groups.forEach((fields, groupFormItemId) => {
    const groupFieldIds = new Set(fields.map((f) => f.taskFieldId));
    let maxInstances = 0;
    submissions.forEach((submission) => {
      const instanceCount = getGroupInstanceIds(submission, groupFieldIds).length;
      if (instanceCount > maxInstances) maxInstances = instanceCount;
    });
    maxInstancesPerGroup.set(groupFormItemId, Math.max(maxInstances, 1));
  });

  const result: ExpandedFormFieldConfig[] = [];
  const fieldOrder = [...formFields];

  const processedGroups = new Set<string>();

  fieldOrder.forEach((field) => {
    if (!field.groupFormItemId) {
      result.push({ ...field, instanceIndex: 0, expandedName: field.name });
    } else if (!processedGroups.has(field.groupFormItemId)) {
      processedGroups.add(field.groupFormItemId);
      const groupFields = groups.get(field.groupFormItemId) || [];
      const maxInst = maxInstancesPerGroup.get(field.groupFormItemId) || 1;
      const groupLabel = field.groupLabel || field.groupTitle || 'Entry';

      for (let i = 0; i < maxInst; i++) {
        groupFields.forEach((gf) => {
          result.push({
            ...gf,
            instanceIndex: i,
            expandedName: maxInst === 1 ? `${groupLabel} - ${gf.name}` : `${groupLabel} ${i + 1} - ${gf.name}`,
          });
        });
      }
    }
  });

  return result;
};

export interface GroupedFieldValues {
  groupInstanceId: string;
  values: FormSubmissionFieldValue[];
}

const OPTION_TRIGGER_KEYS = ['multipleChoiceOptions', 'checkboxOptions', 'dropdownOptions', 'quizTaskFieldOptions'];

interface TriggeredChildRef {
  formItemId: string;
  optionName: string;
}

const byOrder = (a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0);

const getTriggeredChildRefs = (fieldItem: any): TriggeredChildRef[] => {
  const field = fieldItem?.field;
  if (!field) return [];

  const refs: TriggeredChildRef[] = [];
  OPTION_TRIGGER_KEYS.forEach((optionsKey) => {
    const options = field[optionsKey];
    if (!Array.isArray(options)) return;
    [...options].sort(byOrder).forEach((option: any) => {
      const triggerItems = option?.trigger?.items;
      if (!Array.isArray(triggerItems)) return;
      [...triggerItems].sort(byOrder).forEach((triggerItem: any) => {
        const formItemId = triggerItem?.item?.formItemId;
        if (formItemId) refs.push({ formItemId, optionName: option?.name || '' });
      });
    });
  });
  return refs;
};

const buildFieldConfig = (fieldItem: any, extras: Partial<FormFieldConfig> = {}): FormFieldConfig | null => {
  const field = fieldItem?.field;
  if (!field?.taskFieldId) return null;
  return {
    taskFieldId: field.taskFieldId,
    name: field.name,
    fieldType: field.__typename,
    formItemId: fieldItem.formItemId,
    optional: fieldItem.optional ?? undefined,
    ...('minValue' in field ? { minValue: field.minValue } : {}),
    ...('maxValue' in field ? { maxValue: field.maxValue } : {}),
    ...('minLabel' in field ? { minLabel: field.minLabel } : {}),
    ...('maxLabel' in field ? { maxLabel: field.maxLabel } : {}),
    ...extras,
  };
};

/**
 * Conditional follow-ups are stored as flat form items linked to the option that
 * reveals them, so their `order` does not describe where they appear. Rebuild the
 * tree the way the form renders it, so responses read in the sequence submitters saw.
 */
export const buildFormOutline = (formItems: any[]): FormOutlineNode[] => {
  const items = (Array.isArray(formItems) ? formItems : []).filter((item) => item?.formItemId);

  const itemsById = new Map<string, any>();
  items.forEach((item) => itemsById.set(item.formItemId, item));

  const childRefsByParent = new Map<string, TriggeredChildRef[]>();
  const triggeredItemIds = new Set<string>();

  items.forEach((item) => {
    if (item.__typename !== 'VOLUNTEER_FormItemFieldType') return;
    const refs = getTriggeredChildRefs(item).filter((ref) => itemsById.has(ref.formItemId));
    if (refs.length === 0) return;
    childRefsByParent.set(item.formItemId, refs);
    refs.forEach((ref) => triggeredItemIds.add(ref.formItemId));
  });

  const nodes: FormOutlineNode[] = [];
  const emitted = new Set<string>();

  const emit = (item: any, depth: number, triggeredByOptionName?: string) => {
    if (!item || emitted.has(item.formItemId)) return;
    emitted.add(item.formItemId);

    if (item.__typename === 'VOLUNTEER_FormItemFieldType') {
      const field = buildFieldConfig(item, { depth, triggeredByOptionName });
      if (field) nodes.push({ kind: 'field', formItemId: item.formItemId, depth, field });
    } else if (item.__typename === 'VOLUNTEER_FormItemHeadingType') {
      nodes.push({
        kind: 'heading',
        formItemId: item.formItemId,
        depth,
        heading: item.heading || '',
        triggeredByOptionName,
      });
    } else if (item.__typename === 'VOLUNTEER_FormItemRepeatableGroupType') {
      const title = item.title || item.label || 'Group';
      const fields = [...(item.groupItems || [])]
        .sort(byOrder)
        .map((groupItem: any) =>
          groupItem?.__typename === 'VOLUNTEER_FormItemFieldType'
            ? buildFieldConfig(groupItem, {
                depth: depth + 1,
                groupFormItemId: item.formItemId,
                groupTitle: title,
                groupLabel: item.label || undefined,
              })
            : null
        )
        .filter((field: FormFieldConfig | null): field is FormFieldConfig => field !== null);

      nodes.push({
        kind: 'group',
        formItemId: item.formItemId,
        depth,
        title,
        label: item.label || 'Entry',
        fields,
        triggeredByOptionName,
      });
    }

    (childRefsByParent.get(item.formItemId) || []).forEach((ref) =>
      emit(itemsById.get(ref.formItemId), depth + 1, ref.optionName)
    );
  };

  items
    .filter((item) => !triggeredItemIds.has(item.formItemId))
    .sort(byOrder)
    .forEach((item) => emit(item, 0));

  // Items triggered from outside this form still need a home.
  [...items].sort(byOrder).forEach((item) => emit(item, 0));

  return nodes;
};

export const flattenOutlineFields = (outline: FormOutlineNode[]): FormFieldConfig[] => {
  const fields: FormFieldConfig[] = [];
  outline.forEach((node) => {
    if (node.kind === 'field') fields.push(node.field);
    else if (node.kind === 'group') fields.push(...node.fields);
  });
  return fields;
};

/**
 * Split the outline into collapsible sections, one per top level heading.
 */
export const buildOutlineSections = (outline: FormOutlineNode[]): FormOutlineSection[] => {
  const sections: FormOutlineSection[] = [];
  let current: FormOutlineSection = { key: '__intro', nodes: [] };

  outline.forEach((node) => {
    if (node.kind === 'heading' && node.depth === 0) {
      if (current.nodes.length > 0 || current.heading) sections.push(current);
      current = {
        key: node.formItemId,
        heading: { formItemId: node.formItemId, heading: node.heading },
        nodes: [],
      };
      return;
    }
    current.nodes.push(node);
  });

  if (current.nodes.length > 0 || current.heading) sections.push(current);
  return sections;
};

export const groupFieldValuesByInstance = (
  fieldValues: FormSubmissionFieldValue[],
  groupFieldIds: Set<string>
): { ungrouped: FormSubmissionFieldValue[]; grouped: Map<string, GroupedFieldValues> } => {
  const ungrouped: FormSubmissionFieldValue[] = [];
  const grouped = new Map<string, GroupedFieldValues>();

  fieldValues.forEach((fv) => {
    if (fv.groupInstanceId && groupFieldIds.has(fv.taskFieldId)) {
      const existing = grouped.get(fv.groupInstanceId);
      if (existing) {
        existing.values.push(fv);
      } else {
        grouped.set(fv.groupInstanceId, { groupInstanceId: fv.groupInstanceId, values: [fv] });
      }
    } else {
      ungrouped.push(fv);
    }
  });

  return { ungrouped, grouped };
};
